import dateFormat from 'dateformat';
import { groupBy, orderBy, intersection } from 'lodash-es';
import {
  ALL_VALUE,
  DEFAULT_FILTERS,
  DEFAULT_NAVIGATION_PARAMS,
  DISPLAY_MODE,
  FILTER_QUERY_PARAMS,
} from 'site-modules/shared/constants/incentives/electric-car-incentives';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { PUB_STATES } from 'client/constants/pub-states';
import { PROGRAM_TYPES } from 'site-modules/shared/constants/incentives';
import { formatPriceString, getPriceString } from 'site-modules/shared/utils/price-utils';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import {
  checkIsAllValue,
  checkLength,
  maybe,
  wrapIn,
  wrapNotIn,
} from 'site-modules/shared/utils/incentives/gql-incentives-filter';

export function getAvailableTypeOptions(programTypes) {
  const evProgramsCategories = [
    'EV Rebate',
    'Tax Credit',
    'EV Charging',
    'Charger Installation',
    'Municipality Rebate',
    'State Rebate',
    'Utility Rebate',
  ];
  return programTypes
    ? orderBy(
        programTypes.map(programType => ({
          text: programType,
          title: programType,
          typeGroup: evProgramsCategories.includes(programType) ? 'EV Programs' : 'Automaker Programs',
        })),
        ['typeGroup', 'label'],
        ['desc', 'asc']
      )
    : {};
}

export function getAvailableMakeModelOptions(valuesList, allValueText) {
  return [
    { text: allValueText, title: ALL_VALUE, hideValue: true },
    ...orderBy(
      valuesList.map(name => ({
        text: name,
        title: name,
        hideValue: true,
      })),
      ['text']
    ),
  ];
}

export function isUsedPubState(pubStates) {
  return pubStates && pubStates.length === 1 && pubStates[0] === PUB_STATES.USED;
}

export function getEvIncentiveFiltersQuery({
  makeSlug,
  modelSlug,
  programTypes,
  activeDate,
  defaultActiveDate,
  userMsrp,
  pageNum,
  displayMode,
  sortBy,
  engineType,
  transactionType,
  vehCondition,
}) {
  const activeDateString = dateFormat(activeDate, 'yyyy-mm-dd');
  const activeDisplayMode =
    displayMode === DISPLAY_MODE.INCENTIVES ? DISPLAY_MODE.INCENTIVES : DISPLAY_MODE.MODEL_YEARS;

  const query = objectToQueryString({
    [FILTER_QUERY_PARAMS.MAKE]: makeSlug === DEFAULT_FILTERS[FILTER_QUERY_PARAMS.MAKE] ? undefined : makeSlug,
    [FILTER_QUERY_PARAMS.MODEL]: modelSlug === DEFAULT_FILTERS[FILTER_QUERY_PARAMS.MODEL] ? undefined : modelSlug,
    [FILTER_QUERY_PARAMS.PROGRAM_TYPE]: programTypes?.length ? programTypes : undefined,
    [FILTER_QUERY_PARAMS.ACTIVE_DATE]: activeDateString === defaultActiveDate ? undefined : activeDateString,
    [FILTER_QUERY_PARAMS.USER_MSRP]: userMsrp === DEFAULT_FILTERS[FILTER_QUERY_PARAMS.USER_MSRP] ? undefined : userMsrp,
    [FILTER_QUERY_PARAMS.PAGENUM]:
      pageNum === DEFAULT_NAVIGATION_PARAMS[DISPLAY_MODE.MODEL_YEARS][FILTER_QUERY_PARAMS.PAGENUM]
        ? undefined
        : pageNum,
    [FILTER_QUERY_PARAMS.DISPLAY_MODE]:
      displayMode === DEFAULT_FILTERS[FILTER_QUERY_PARAMS.DISPLAY_MODE] ? undefined : displayMode,
    [FILTER_QUERY_PARAMS.SORT_BY]:
      sortBy === DEFAULT_NAVIGATION_PARAMS[activeDisplayMode][FILTER_QUERY_PARAMS.SORT_BY] ? undefined : sortBy,
    [FILTER_QUERY_PARAMS.ENGINE_TYPE]:
      engineType === DEFAULT_FILTERS[FILTER_QUERY_PARAMS.ENGINE_TYPE] ? undefined : engineType,
    [FILTER_QUERY_PARAMS.TRANSACTION_TYPE]:
      transactionType === DEFAULT_FILTERS[FILTER_QUERY_PARAMS.TRANSACTION_TYPE] ? undefined : transactionType,
    [FILTER_QUERY_PARAMS.VEHICLE_CONDITION]:
      vehCondition === DEFAULT_FILTERS[FILTER_QUERY_PARAMS.VEHICLE_CONDITION] ? undefined : vehCondition,
  });

  return query ? `?${query}` : '';
}

export function orderNAValuesLast({ arr, field }) {
  return arr.filter(value => value && value[field]).concat(arr.filter(value => value && !value[field]));
}

export const getOrderedIncentives = incentives => {
  const orderedByProgramTypeIncentivesObject = groupBy(incentives, 'programType');

  return orderBy(
    Object.keys(orderedByProgramTypeIncentivesObject).map(key => ({
      programType: key,
      availableCount: orderedByProgramTypeIncentivesObject[key].length,
      totalAmount: orderedByProgramTypeIncentivesObject[key].reduce((acc, { rebateAmount }) => acc + rebateAmount, 0),
      orderedIncentives: orderNAValuesLast({
        arr: orderBy(orderedByProgramTypeIncentivesObject[key], ['rebateAmount', 'subtype'], ['desc', 'asc']),
        field: 'rebateAmount',
      }),
    })),
    'totalAmount',
    'desc'
  );
};

/**
 * @param {(string | Array)} value
 * @returns {boolean}
 */
export const isDefaultValue = value => value === ALL_VALUE || value?.length === 0;

export const getVehicleRebatesValue = ({ rebates, incentives }) => {
  if (rebates === 0) {
    return incentives.some(
      ({ programType }) =>
        programType !== PROGRAM_TYPES.EV_CHARGING && programType !== PROGRAM_TYPES.CHARGER_INSTALLATION
    )
      ? 'Other'
      : formatPriceString(rebates);
  }

  return getPriceString(rebates);
};
export const getChargingRebatesValue = ({ rebates, incentives }) => {
  if (rebates === 0) {
    return incentives.some(({ programType }) =>
      [PROGRAM_TYPES.EV_CHARGING, PROGRAM_TYPES.CHARGER_INSTALLATION].includes(programType)
    )
      ? 'Other'
      : formatPriceString(rebates);
  }

  return getPriceString(rebates);
};
export const isAvailableToAllIncentive = ({ primary, compatibleIncentiveIds, programType }) =>
  primary ||
  ([
    PROGRAM_TYPES.EV_REBATE,
    PROGRAM_TYPES.EV_CHARGING,
    PROGRAM_TYPES.TAX_CREDIT,
    PROGRAM_TYPES.UTILITY_REBATE,
    PROGRAM_TYPES.MUNICIPALITY_REBATE,
    PROGRAM_TYPES.CHARGER_INSTALLATION,
    PROGRAM_TYPES.STATE_REBATE,
  ].includes(programType) &&
    compatibleIncentiveIds.length === 0);

export const getProgramTypesFilter = programTypes => {
  const evPrograms = [PROGRAM_TYPES.EV_REBATE, PROGRAM_TYPES.TAX_CREDIT, PROGRAM_TYPES.CHARGER_INSTALLATION];

  if (!programTypes?.length) {
    return evPrograms;
  }

  return intersection(programTypes, evPrograms);
};

const LEASE_ELIGIBLE_SUBTYPE_IDS = [605, 655, 606, 625, 624, 626, 664, 19, 605, 626, 603, 624];

const getBeforeWizardSupportCopyShop3527 = ({
  transactionTypeFilter,
  primary,
  compatibleIncentiveIds,
  rebateOverridesByEligibility,
  subtypeId,
}) => {
  const SUPPORT_COPY_MAP = {
    AvailableToAll: { text: 'Available to all', className: 'text-blue-50 medium fst-italic fw-normal' },
    CheckEligibility: { text: 'Check eligibility', className: 'text-blue-50 medium fst-italic fw-normal' },
    RestrictionsApply: { text: 'Restrictions apply', className: 'text-cool-gray-40 medium fst-italic fw-normal' },
  };

  const cashFinanceHandler = () => {
    if (primary || (!compatibleIncentiveIds.length && !rebateOverridesByEligibility.length)) {
      return SUPPORT_COPY_MAP.AvailableToAll;
    }
    if (!compatibleIncentiveIds.length && rebateOverridesByEligibility.length) {
      return SUPPORT_COPY_MAP.CheckEligibility;
    }

    return SUPPORT_COPY_MAP.RestrictionsApply;
  };

  const leaseHandler = () => {
    if (
      LEASE_ELIGIBLE_SUBTYPE_IDS.includes(subtypeId) ||
      (!compatibleIncentiveIds.length && !rebateOverridesByEligibility.length)
    ) {
      return SUPPORT_COPY_MAP.AvailableToAll;
    }
    if (!compatibleIncentiveIds.length && rebateOverridesByEligibility.length) {
      return SUPPORT_COPY_MAP.CheckEligibility;
    }
    return SUPPORT_COPY_MAP.RestrictionsApply;
  };

  const BEFORE_WIZARD_MAP = {
    CASH: cashFinanceHandler,
    FINANCE: cashFinanceHandler,
    LEASE: leaseHandler,
  };

  return BEFORE_WIZARD_MAP[transactionTypeFilter]();
};

const getPostWizardSupportCopyShop3527 = ({ transactionTypeFilter, primary, compatibleIncentiveIds, subtypeId }) => {
  const SUPPORT_COPY_MAP = {
    AvailableToYou: { text: 'Available to you', className: 'text-blue-50 medium fst-italic fw-normal' },
    RestrictionsApply: { text: 'Restrictions apply', className: 'text-cool-gray-40 medium fst-italic fw-normal' },
  };

  const postCashFinanceHandler = () =>
    primary || !compatibleIncentiveIds.length ? SUPPORT_COPY_MAP.AvailableToYou : SUPPORT_COPY_MAP.RestrictionsApply;

  const postLeaseHandler = () =>
    LEASE_ELIGIBLE_SUBTYPE_IDS.includes(subtypeId) || !compatibleIncentiveIds.length
      ? SUPPORT_COPY_MAP.AvailableToYou
      : SUPPORT_COPY_MAP.RestrictionsApply;

  const POST_WIZARD_MAP = {
    CASH: postCashFinanceHandler,
    FINANCE: postCashFinanceHandler,
    LEASE: postLeaseHandler,
  };

  return POST_WIZARD_MAP[transactionTypeFilter]();
};

export const getSupportCopyShop3527 = ({
  transactionTypeFilter,
  isCompletedWizardFlow,
  primary,
  compatibleIncentiveIds,
  rebateOverridesByEligibility,
  subtypeId,
}) =>
  isCompletedWizardFlow
    ? getPostWizardSupportCopyShop3527({
        transactionTypeFilter,
        primary,
        compatibleIncentiveIds,
        subtypeId,
      })
    : getBeforeWizardSupportCopyShop3527({
        transactionTypeFilter,
        primary,
        compatibleIncentiveIds,
        subtypeId,
        rebateOverridesByEligibility,
      });

const compareProgramTypesFilters = excludedProgramTypes => programTypes => {
  if (!excludedProgramTypes) return programTypes;
  return programTypes.filter(programType => !excludedProgramTypes.includes(programType));
};
const wrapProgramTypes = excludedProgramTypes => programTypes => {
  if (!excludedProgramTypes && !programTypes.length) {
    return undefined;
  }
  return !programTypes.length && excludedProgramTypes.length ? wrapNotIn(excludedProgramTypes) : wrapIn(programTypes);
};

export const getGqlIncentiveFilters = (incentivesFilter = {}, excludedFilters = {}) => {
  const {
    zip,
    makeSlug,
    modelSlug,
    year,
    programTypes,
    engineType,
    activeDate,
    transactionType,
    vehCondition,
    styleIds,
    types,
  } = incentivesFilter;
  return {
    zip,
    activeDate,
    engineTypes: [engineType],
    inventoryCodes: [vehCondition],
    transactionTypes: wrapIn(transactionType),
    makeSlugs: maybe(makeSlug).map(checkIsAllValue).map(makeNiceName).map(wrapIn).unpack(),
    modelSlugs: maybe(modelSlug).map(checkIsAllValue).map(makeNiceName).map(wrapIn).unpack(),
    years: maybe(year).map(Number).map(wrapIn).unpack(),
    programTypesFilter: maybe(programTypes)
      .map(compareProgramTypesFilters(excludedFilters.programTypes))
      .map(wrapProgramTypes(excludedFilters.programTypes))
      .unpack(),
    types: maybe(types).map(checkLength).map(wrapNotIn).unpack(),
    styleIds: maybe(styleIds).map(checkLength).unpack(),
    subtypeIds: maybe(excludedFilters.subtypeIds).map(checkLength).map(wrapNotIn).unpack(),
  };
};
